/**
 * MSAL bootstrap — creates the singleton PublicClientApplication and
 * exports initMsal() which must be awaited in main.tsx before React renders.
 *
 * Ordering guaranteed by §11.8 of CC_LLD_PRD_0007.md:
 *   initialize() → handleRedirectPromise() → setActiveAccount()
 *
 * The one-time interactive-sign-in handshake (GET /api/v1/auth/session/)
 * is called ONLY in the handleRedirectPromise success branch — never on
 * silent renewal or ordinary bearer traffic.
 *
 * After initMsal() resolves, getActiveAccount() is reliable; the HTTP
 * interceptor and Socket.IO auth callback depend on this guarantee.
 */

import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig, apiScopes } from '@/config/msalConfig'

/**
 * Singleton MSAL instance shared by the React provider (MsalProvider),
 * the HTTP interceptor (api.ts), and the Socket.IO auth callback
 * (simpleWebSocket.ts).
 */
export const msalInstance = new PublicClientApplication(msalConfig)

/**
 * Bootstrap MSAL once before the React tree renders.
 *
 * Steps:
 * 1. initialize() — internal MSAL setup (storage, cache hydration)
 * 2. handleRedirectPromise() — consumes the auth-code redirect if present
 * 3a. Redirect result + account → setActiveAccount (freshly signed in)
 *     → call GET /api/v1/auth/session/ ONCE (interactive-sign-in handshake)
 * 3b. No redirect result but cached accounts → setActiveAccount(accounts[0])
 *     (single-account policy; no backend handshake — this is a page reload,
 *     not an interactive sign-in)
 *
 * The api import is deferred inside the function body to break the circular
 * dependency: api.ts → msalInstance (msal.ts) → api (msal.ts → api.ts).
 * The function runs once at startup before axios interceptors are exercised,
 * so the deferred import is always resolved in time.
 */
export async function initMsal(): Promise<void> {
  await msalInstance.initialize()

  const result = await msalInstance.handleRedirectPromise()

  if (result?.account) {
    // A real interactive login just completed — set the active account and
    // fire the one-time backend handshake that records last_login_at and
    // emits auth_user_signed_in. This branch runs at most once per
    // interactive redirect, never on acquireTokenSilent renewals.
    msalInstance.setActiveAccount(result.account)

    // Deferred import avoids circular dep at module evaluation time.
    const { api } = await import('@/services/api')
    await api.auth.getSession()
  } else {
    // Page reload with cached credentials — restore the active account so
    // getActiveAccount() is non-null for the interceptor and socket callback.
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      // Single-account policy: pick the first cached account (most recently
      // active is index 0 per MSAL internal ordering). Multi-account UX is
      // out of scope.
      msalInstance.setActiveAccount(accounts[0])
    }
  }
}

/**
 * Acquire an Entra access token for the CC API.
 *
 * Returns null when no account is active (the caller proceeds unauthenticated
 * and the backend answers 401). Throws InteractionRequiredAuthError when an
 * interactive redirect is required — callers handle that via
 * redirectForApiToken(). Shared by the HTTP interceptor (api.ts) and the
 * Socket.IO auth callback (simpleWebSocket.ts) so the silent-acquisition
 * policy lives in one place.
 */
export async function acquireApiToken(
  opts?: { forceRefresh?: boolean }
): Promise<string | null> {
  let account = msalInstance.getActiveAccount()
  if (!account) {
    // An account can exist in the cache without being marked active (a
    // transient state before initMsal's restore, or a multi-tab race). Adopt
    // it so a signed-in session always attempts silent acquisition rather than
    // returning null — which the interceptor treats as the pre-login state and
    // would send the request unauthenticated to a now default-deny route
    // (PRD-0044). null is reserved for "genuinely no account".
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) return null
    account = accounts[0]
    msalInstance.setActiveAccount(account)
  }
  // forceRefresh bypasses the cached access token and renews from the refresh
  // token (or interactive fallback) — used by the HTTP 401-recovery path so a
  // token the API rejected is replaced rather than handed back from cache.
  const result = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account,
    forceRefresh: opts?.forceRefresh ?? false,
  })
  return result.accessToken
}

let redirectInFlight = false

/**
 * Send the user through an interactive redirect to obtain a fresh API token.
 * Used when acquireApiToken() throws InteractionRequiredAuthError.
 *
 * Idempotent within a page load: concurrent callers — a dashboard firing
 * several queries at once that all hit an expired silent token — must initiate
 * exactly one redirect; a second acquireTokenRedirect while one is in flight
 * throws BrowserAuthError(interaction_in_progress).
 */
export async function redirectForApiToken(): Promise<void> {
  if (redirectInFlight) return
  redirectInFlight = true
  try {
    await msalInstance.acquireTokenRedirect({ scopes: apiScopes })
  } catch (err) {
    redirectInFlight = false
    throw err
  }
}
